import { PlusSquareIcon } from '@phosphor-icons/react';
import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { uploadFileToS3 } from '@/helpers';
import { PageHeader } from '@/modules/common';
import LoadingContainer from '@/modules/common/components/loading-container';
import { Routes } from '@/router/routes';

import { useCreateGcrs, useGcrFormOptions } from '../api';
import { GcrForm } from '../components';
import { GcrFormValues } from '../validations';

export const NewGcrPage: FC = () => {
  const navigate = useNavigate();

  const { createGcrMutation, uploadUrlDataMutation } = useCreateGcrs();

  const { optionsData, isFetchingPrinters } = useGcrFormOptions();

  const saveGcr = async (values: GcrFormValues) => {
    try {
      let key: string | undefined;

      if (values.filePublicUrl instanceof File) {
        const { url, key: uploadKey } =
          await uploadUrlDataMutation.mutateAsync();
        await uploadFileToS3({ file: values.filePublicUrl, uploadUrl: url });
        key = uploadKey;
      }

      await createGcrMutation.mutateAsync({
        id: crypto.randomUUID(),
        name: values.name,
        combinations: values.combinations.map((combination) => ({
          colorId: values.colorId,
          printerId: values.printerId,
          typeId: combination.typeId,
          sizeId: combination.sizeId,
        })),
        ...(key && { key }),
      });

      navigate(Routes.Gcrs, { replace: true });

      toast.success(`GCR file “${values.name}” added successfully!`);
    } catch (error) {
      toast.error(error?.message || 'Uh oh! Something went wrong.');
    }
  };

  return (
    <LoadingContainer loading={isFetchingPrinters}>
      <GcrForm onSubmit={saveGcr} options={optionsData}>
        <PageHeader
          title="Add New GCR File"
          asChildPage
          backPath={Routes.Gcrs}
          actions={[
            <Button
              key="create-gcr"
              type="submit"
              data-testid="create-gcr-button"
              isLoading={
                createGcrMutation.isPending || uploadUrlDataMutation.isPending
              }
              startIcon={<PlusSquareIcon />}
            >
              Save File
            </Button>,
          ]}
        />
      </GcrForm>
    </LoadingContainer>
  );
};
